import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const API = '/api';
const inputStyle = { width: '100%' };

const FORMATS = ['5v5', '6v6', '7v7', '8v8', '11v11'];
const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Professional'];
const CURRENCIES = ['USD', 'AED', 'NGN', 'GBP', 'EUR'];

const INIT = {
  groupId: '', pitchId: '', title: '', description: '',
  kickoffAt: '', format: '5v5', playersPerSide: 5,
  skillLevel: 'Intermediate', hostingFee: 0, currency: 'USD',
  needsGoalkeepers: false, needsReferee: false, needsBibs: false,
  fillThreshold: 50,
};


function GroupSearch({ groups, loading, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  React.useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = groups.filter(g => {
    const name = (g.name || g.title || '').toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const selected = groups.find(g => g.id === value);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(o => !o)}
        className="search-input"
        style={{ width: '100%', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxSizing: 'border-box' }}
      >
        <span style={{ color: selected ? 'var(--text-primary)' : 'var(--text-dim)', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {loading ? 'Loading groups...' : selected ? (selected.name || selected.title) : 'Select a group'}
        </span>
        <span style={{ color: 'var(--text-dim)', fontSize: 10, flexShrink: 0, marginLeft: 8 }}>▼</span>
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 300, marginTop: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
            <input
              ref={inputRef}
              className="search-input"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 12 }}
              placeholder="Search groups..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <div
              onMouseDown={() => { onChange(''); setOpen(false); setSearch(''); }}
              style={{ padding: '9px 14px', fontSize: 13, color: 'var(--text-dim)', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
            >
              Select a group
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>No groups found</div>
            ) : filtered.map(g => (
              <div
                key={g.id}
                onMouseDown={() => { onChange(g.id); setOpen(false); setSearch(''); }}
                style={{ padding: '9px 14px', fontSize: 13, cursor: 'pointer', color: g.id === value ? 'var(--accent)' : 'var(--text-primary)', background: g.id === value ? 'rgba(167,244,50,0.08)' : 'transparent' }}
              >
                {g.name || g.title}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CommunityMatches({ toast }) {
  const { token } = useApp();
  const [tab, setTab] = useState('create');
  const [form, setForm] = useState(INIT);
  const [submitting, setSubmitting] = useState(false);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingPitches, setLoadingPitches] = useState(false);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const r = await fetch(API + '/app/communities?limit=500', { headers: { Authorization: 'Bearer ' + token } });
      const d = await r.json();
      const raw = d.data;
      setGroups(Array.isArray(raw) ? raw : (raw?.groups || raw?.communities || []));
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setLoadingGroups(false); }
  }, [token, toast]);

  const loadPitches = useCallback(async () => {
    setLoadingPitches(true);
    try {
      const r = await fetch(API + '/app/pitches?limit=500', { headers: { Authorization: 'Bearer ' + token } });
      const d = await r.json();
      setPitches(Array.isArray(d.data) ? d.data : []);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setLoadingPitches(false); }
  }, [token, toast]);

  const loadMatches = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(API + '/app/community-matches?limit=50', { headers: { Authorization: 'Bearer ' + token } });
      const d = await r.json();
      setMatches(Array.isArray(d.data) ? d.data : []);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setLoading(false); }
  }, [token, toast]);

  useEffect(() => {
    loadGroups();
    loadPitches();
  }, [loadGroups, loadPitches]);

  useEffect(() => {
    if (tab === 'list') loadMatches();
  }, [tab, loadMatches]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.groupId) { toast?.('Select a group', 'error'); return; }
    if (!form.pitchId) { toast?.('Select a pitch', 'error'); return; }
    if (!form.kickoffAt) { toast?.('Set kickoff time', 'error'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(API + '/app/community-matches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ ...form, playersPerSide: parseInt(form.playersPerSide), hostingFee: parseFloat(form.hostingFee), fillThreshold: parseInt(form.fillThreshold) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed');
      toast?.(d.message || 'Community match created!');
      setForm(INIT);
      setTab('list');
    } catch (err) { toast?.(err.message, 'error'); }
    finally { setSubmitting(false); }
  };

  const statusColor = s => ({ pending: '#FF9800', accepted: '#A7F432', declined: '#f44336', cancelled: '#888' })[s] || '#888';

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 500, color: 'var(--text-primary)', margin: 0 }}>Community Matches</h1>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 4 }}>Create matches for community groups — nearby organizers will be notified to host</p>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
        {[['create', 'Create Match'], ['list', 'All Requests']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding: '9px 20px', fontSize: 13, border: 'none', cursor: 'pointer', background: 'none',
            color: tab === id ? 'var(--accent)' : 'var(--text-dim)',
            borderBottom: tab === id ? '2px solid var(--accent)' : '2px solid transparent',
            fontWeight: tab === id ? 600 : 400, fontFamily: 'var(--font-body)',
          }}>{label}</button>
        ))}
      </div>

      {tab === 'create' && (
        <form className="card" onSubmit={handleSubmit} style={{ padding: '24px 28px', marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>New community match request</div>
          <div style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 20, lineHeight: 1.55 }}>
            Select a group and pitch. Organizers near the pitch will receive a push notification and the first to accept becomes the host.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Group *</label>
              <GroupSearch groups={groups} loading={loadingGroups} value={form.groupId} onChange={v => setF('groupId', v)} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Pitch *</label>
              <select className="search-input" style={inputStyle} value={form.pitchId} onChange={e => setF('pitchId', e.target.value)}>
                <option value="">{loadingPitches ? 'Loading...' : 'Select a pitch'}</option>
                {pitches.map(p => <option key={p.id} value={p.id}>{p.name}{p.city ? ` — ${p.city}` : ''}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Title</label>
            <input className="search-input" style={inputStyle} value={form.title} onChange={e => setF('title', e.target.value)} placeholder="e.g. Friday Night 5v5 — Dubai" />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Description</label>
            <textarea className="search-input" style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} rows={3}
              value={form.description} onChange={e => setF('description', e.target.value)}
              placeholder="Message shown to organizers in the push notification..." />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Kickoff Date & Time *</label>
              <input className="search-input" style={inputStyle} type="datetime-local" value={form.kickoffAt} onChange={e => setF('kickoffAt', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Format</label>
              <select className="search-input" style={inputStyle} value={form.format} onChange={e => setF('format', e.target.value)}>
                {FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Players per side</label>
              <input className="search-input" style={inputStyle} type="number" min={3} max={11} value={form.playersPerSide} onChange={e => setF('playersPerSide', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Skill level</label>
              <select className="search-input" style={inputStyle} value={form.skillLevel} onChange={e => setF('skillLevel', e.target.value)}>
                {SKILL_LEVELS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Fill threshold %</label>
              <input className="search-input" style={inputStyle} type="number" min={10} max={100} value={form.fillThreshold} onChange={e => setF('fillThreshold', e.target.value)} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Hosting fee</label>
              <input className="search-input" style={inputStyle} type="number" min={0} value={form.hostingFee} onChange={e => setF('hostingFee', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Currency</label>
              <select className="search-input" style={inputStyle} value={form.currency} onChange={e => setF('currency', e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
            {[['needsGoalkeepers', 'Needs goalkeepers'], ['needsReferee', 'Needs referee'], ['needsBibs', 'Needs bibs']].map(([k, label]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-dim)', cursor: 'pointer' }}>
                <input type="checkbox" checked={form[k]} onChange={e => setF(k, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => setForm(INIT)} disabled={submitting}>Clear</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send to nearby organizers'}
            </button>
          </div>
        </form>
      )}

      {tab === 'list' && (
        <div>
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>Loading...</div>
          ) : matches.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🏟️</div>
              <div>No community matches created yet</div>
              <button className="btn btn-primary" onClick={() => setTab('create')} style={{ marginTop: 16 }}>Create first match</button>
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <div className="card-header" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                <span className="card-title">ALL COMMUNITY MATCH REQUESTS</span>
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{matches.length} total</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Title</th><th>Pitch</th><th>Kickoff</th><th>Format</th><th>Fee</th><th>Status</th><th>Organizer</th></tr>
                  </thead>
                  <tbody>
                    {matches.map(m => (
                      <tr key={m.id}>
                        <td style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{m.title}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-dim)' }}>{m.pitch?.name || '—'}{m.pitch?.city ? ` · ${m.pitch.city}` : ''}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-dim)' }}>{m.kickoffAt ? new Date(m.kickoffAt).toLocaleString() : '—'}</td>
                        <td style={{ fontSize: 12 }}>{m.format}</td>
                        <td style={{ fontSize: 12 }}>{m.metadata?.hostingFee ? `${m.metadata.currency || 'USD'} ${m.metadata.hostingFee}` : 'Free'}</td>
                        <td><span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, background: statusColor(m.status) + '20', color: statusColor(m.status), border: '0.5px solid ' + statusColor(m.status) }}>{m.status}</span></td>
                        <td style={{ fontSize: 12, color: 'var(--text-dim)' }}>{m.organizer?.user ? `${m.organizer.user.firstName} ${m.organizer.user.lastName}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
